import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Factory, Gauge, Wrench, AlertOctagon, Flame, Zap, Boxes, ShieldCheck,
  TrendingUp, Activity, FileBarChart, ClipboardCheck, Truck
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip,
         ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function KPI({ icon: Icon, label, value, unit, accent = "sky", trend, testid }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700 border-sky-100",
    green: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    red: "bg-red-50 text-red-700 border-red-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <Card data-testid={testid} className="p-5 hover:shadow-md hover:border-slate-300 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap text-slate-500">{label}</div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <div className="text-3xl font-light tracking-tight text-slate-900" style={{ fontFamily: "Chivo" }}>
              {value}
            </div>
            {unit && <div className="text-xs text-slate-500 font-medium">{unit}</div>}
          </div>
          {trend && <div className="mt-1 text-xs text-slate-500">{trend}</div>}
        </div>
        <div className={`h-9 w-9 rounded-md border flex items-center justify-center ${tones[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const [kpi, setKpi] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (user?.role === "customer" && r.data.length) setPid(r.data[0].id);
    });
  }, [user]);

  useEffect(() => {
    const params = pid !== "all" ? { project_id: pid } : {};
    api.get("/dashboard/kpis", { params }).then((r) => setKpi(r.data));
    api.get("/dashboard/trends", { params: { ...params, days: 30 } }).then((r) => setTrend(r.data));
  }, [pid]);

  return (
    <AppLayout
      title="Operations Overview"
      subtitle="Real-time view of plant production, maintenance, inventory and safety."
      actions={
        user?.role !== "customer" && (
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-56" data-testid="dashboard-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )
      }
    >
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <KPI testid="kpi-card-feedstock-received" icon={Truck} label="Feedstock Received" value={kpi?.feedstock_received_today ?? "—"} unit="MT" accent="sky" trend={`MTD ${(kpi?.feedstock_received_month||0).toLocaleString("en-IN")} MT`} />
        <KPI testid="kpi-card-feedstock-processed" icon={Factory} label="Feedstock Processed" value={kpi?.feedstock_processed_today ?? "—"} unit="MT" accent="sky" trend={`MTD ${(kpi?.feedstock_processed_month||0).toLocaleString("en-IN")} MT`} />
        <KPI testid="kpi-card-yard-stock" icon={Boxes} label="Feedstock Yard Stock" value={(kpi?.feedstock_yard_stock ?? 0).toLocaleString("en-IN")} unit="MT" accent="amber" trend="As on date" />
        <KPI testid="kpi-card-today-production" icon={TrendingUp} label="AFR Produced (Today)" value={kpi?.today_production ?? "—"} unit="MT" accent="green" trend={`MTD ${(kpi?.month_production||0).toLocaleString("en-IN")} MT`} />
        <KPI testid="kpi-card-energy-delivered" icon={Flame} label="Energy Delivered" value={(kpi?.energy_delivered_mtd ?? 0).toLocaleString("en-IN")} unit="MKcal (MTD)" accent="red" trend={`Today ${kpi?.energy_delivered_today ?? 0} · YTD ${(kpi?.energy_delivered_ytd||0).toLocaleString("en-IN")}`} />
        <KPI testid="kpi-card-recovery" icon={Activity} label="Material Recovery" value={kpi?.material_recovery_pct ?? "—"} unit="%" accent="green" trend={`Ferrous ${kpi?.ferrous_recovery_pct ?? 0}% · Misc Loss ${kpi?.misc_loss_pct ?? 0}%`} />
        <KPI testid="kpi-card-weighted-ncv" icon={Gauge} label="Wtd Avg NCV-AFR" value={kpi?.weighted_ncv_afr ?? "—"} unit="Kcal/kg" accent="sky" trend={`Moist ${kpi?.weighted_moisture_afr ?? 0}% · Ash ${kpi?.weighted_ash_afr ?? 0}%`} />
        <KPI testid="kpi-card-fuel-per-mt" icon={Flame} label="Fuel / MT" value={kpi?.fuel_per_mt_month ?? "—"} unit="L/MT" accent="amber" trend="MTD" />
        <KPI testid="kpi-card-electricity-per-mt" icon={Zap} label="Electricity / MT" value={kpi?.electricity_per_mt_month ?? "—"} unit="kWh/MT" accent="sky" trend="MTD" />
        <KPI testid="kpi-card-equipment-availability" icon={Gauge} label="Equipment Avail." value={kpi?.equipment_availability ?? "—"} unit="%" accent="green" />
        <KPI testid="kpi-card-pm-compliance" icon={ClipboardCheck} label="PM Compliance" value={kpi?.pm_compliance ?? "—"} unit="%" accent="green" />
        <KPI testid="kpi-card-maintenance-pending" icon={Wrench} label="Job Orders Pending" value={kpi?.maintenance_pending ?? "—"} accent="amber" />
        <KPI testid="kpi-card-breakdowns" icon={AlertOctagon} label="Breakdowns (M)" value={kpi?.breakdowns_month ?? "—"} accent="red" />
        <KPI testid="kpi-card-critical-low" icon={Boxes} label="Critical Spares Low" value={kpi?.critical_spares_low ?? "—"} accent="red" />
        <KPI testid="kpi-card-safety-score" icon={ShieldCheck} label="Safety Score" value={kpi?.safety_score ?? "—"} unit="/100" accent="green" />
        <KPI testid="kpi-card-incidents" icon={Activity} label="Incidents (M)" value={kpi?.incident_count_month ?? "—"} accent="red" />
      </div>

      {/* Material Balance card */}
      <Card className="p-5 mb-6" data-testid="dashboard-material-balance">
        <div className="label-cap text-slate-500">Material Balance · Month-to-Date</div>
        <div className="text-lg font-semibold mt-0.5 mb-4" style={{ fontFamily: "Chivo" }}>Plant Material Flow (MT)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {[
            ["Feedstock Received", kpi?.feedstock_received_month],
            ["Feedstock Processed", kpi?.feedstock_processed_month],
            ["Yard Stock (as on date)", kpi?.feedstock_yard_stock],
            ["AFR Produced", kpi?.afr_produced_month],
            ["Ferrous Recovered", kpi?.ferrous_recovery_pct, "%"],
            ["Misc. Losses", kpi?.misc_loss_pct, "%"],
            ["Recovery", kpi?.material_recovery_pct, "%"],
          ].map(([l, v, u]) => (
            <div key={l} className="rounded-md border bg-slate-50 px-3 py-2">
              <div className="label-cap text-slate-500 text-[10px]">{l}</div>
              <div className="font-mono font-semibold text-slate-800 mt-1">{(v ?? 0).toLocaleString("en-IN")}{u ? ` ${u}` : " MT"}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 xl:col-span-2" data-testid="chart-card-production">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-cap text-slate-500">Production Trend · 30 days</div>
              <div className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Chivo" }}>Feedstock vs AFR Output</div>
            </div>
            <Badge variant="outline" className="text-xs">Tonnes</Badge>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="g-afr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-fs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#475569" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#475569" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="feedstock" stroke="#475569" fill="url(#g-fs)" name="Feedstock Received (MT)" />
              <Area type="monotone" dataKey="afr" stroke="#0EA5E9" fill="url(#g-afr)" name="AFR Produced (MT)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5" data-testid="chart-card-ncv">
          <div className="label-cap text-slate-500">NCV & Moisture Trend</div>
          <div className="text-lg font-semibold mt-0.5 mb-4" style={{ fontFamily: "Chivo" }}>Quality Parameters</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" dataKey="ncv" stroke="#0C4A6E" strokeWidth={2} dot={false} name="NCV (kcal/kg)" />
              <Line yAxisId="r" dataKey="moisture" stroke="#D97706" strokeWidth={2} dot={false} name="Moisture %" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 xl:col-span-2" data-testid="chart-card-power">
          <div className="label-cap text-slate-500">Power & Fuel Consumption</div>
          <div className="text-lg font-semibold mt-0.5 mb-4" style={{ fontFamily: "Chivo" }}>Energy Footprint</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="electricity" fill="#0EA5E9" name="Electricity (kWh)" />
              <Bar yAxisId="r" dataKey="fuel" fill="#D97706" name="Fuel (L)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5" data-testid="chart-card-summary">
          <div className="label-cap text-slate-500">Plant Health Summary</div>
          <div className="text-lg font-semibold mt-0.5 mb-4" style={{ fontFamily: "Chivo" }}>Key Indicators</div>
          <div className="space-y-3">
            {[
              ["Plant Utilization", `${kpi?.plant_utilization ?? 0}%`, "sky"],
              ["Downtime (Month)", `${(kpi?.downtime_month || 0).toFixed(1)} hrs`, "amber"],
              ["Near Miss (Month)", kpi?.near_miss_month ?? 0, "amber"],
              ["PTW Open", kpi?.ptw_open ?? 0, "slate"],
              ["Feedstock Received (M)", `${(kpi?.feedstock_received_month || 0).toFixed(1)} T`, "slate"],
            ].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-600">{l}</span>
                <span className="text-sm font-semibold text-slate-900 font-mono">{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
